import {
  Component,
  OnInit,
  signal,
  ViewChild,
  inject,
  computed,
  TemplateRef,
  WritableSignal,
  DestroyRef
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ServiceScheduleService} from "../../../services/service-schedule.service";
import {SessionService} from "../../../services/session.service";
import {Permissions} from "../../../enums/permissions";
import {ActivatedRoute} from "@angular/router";
import {TableParams} from "../../../interfaces/table/table-params";
import {TableData} from "../../../interfaces/table/table-data";
import {auditTime, catchError, debounceTime, distinctUntilChanged, of, Subject, switchMap, tap} from "rxjs";
import {FormBuilder, FormGroup} from "@angular/forms";
import {ObjectUtils} from "../../../utils/object-utils";
import {DateUtils} from "../../../utils/date-utils";
import {constructServiceScheduleColumns, ServiceScheduleTableColumn} from "./schedule-table.config";
import {ToastService} from "../../../services/toast.service";
import {JobsService} from "../../../services/jobs.service";
import {TableAction} from "../../../interfaces/table/table-action";
import {UserStorageService} from "../../../services/user-storage.service";

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
  standalone: false
})
export class DashboardComponent implements OnInit {
  upcomingCurrentPage = signal<number>(1);
  pastDueCurrentPage = signal<number>(1);
  upcomingTableParams: TableParams = {currentPage: 1, pageSize: 10, sortParams: null};
  pastDueTableParams: TableParams = {currentPage: 1, pageSize: 10, sortParams: null};

  upcomingTableData = signal<TableData>({data: [], total: 0});
  pastDueTableData = signal<TableData>({data: [], total: 0});

  loadingUpcomingData = signal<boolean>(true);
  loadingPastDueData = signal<boolean>(true);

  loadUpcoming$ = new Subject<{ tableParams: TableParams, filterParams: any }>();
  loadPastDue$ = new Subject<{ tableParams: TableParams, filterParams: any }>();

  @ViewChild('serialNumberColumn', {static: true}) serialNumberColumn!: TemplateRef<any>;
  @ViewChild('nextPmColumn', {static: true}) nextPmColumn!: TemplateRef<any>;

  tableColumns = computed(() =>
    constructServiceScheduleColumns(this.serialNumberColumn, this.nextPmColumn));
  readonly tableActions: TableAction[] = [
    {
      displayName: 'Add to Service Request',
      execute: (row) => {
        const result = this.userStorageService.addToStorageServiceRequest({'origin': 'service_schedule', ...row});
        this.toastService.showToast(result.message, result.type);
      },
      textColor: '#3f83f8'
    }
  ];

  filterForm!: FormGroup;
  readonly allLocationsOption = {location_id: 0, location_name: 'All Locations'};
  locations: any[] = [this.allLocationsOption];
  currentUser: any;
  activeTab = signal<'past-due' | 'upcoming'>('past-due');

  protected readonly DashboardTableColumn = ServiceScheduleTableColumn;
  protected readonly Permission = Permissions;
  private readonly scheduleService = inject(ServiceScheduleService);
  private readonly jobService = inject(JobsService);
  private readonly sessionService = inject(SessionService);
  private readonly activeRouter = inject(ActivatedRoute);
  private readonly userStorageService = inject(UserStorageService);
  private readonly fb = inject(FormBuilder);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.sessionService.currentUser
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => this.currentUser = res.user);
  }

  async ngOnInit(): Promise<void> {
    this.initFilterForm();

    this.initServiceSchedulesObservable(
      this.loadPastDue$,
      this.pastDueTableData,
      this.loadingPastDueData,
      false,
      this.upcomingTableData,
      this.loadingUpcomingData,
      'upcoming'
    );
    this.initServiceSchedulesObservable(
      this.loadUpcoming$,
      this.upcomingTableData,
      this.loadingUpcomingData,
      true,
      this.pastDueTableData,
      this.loadingPastDueData,
      'past-due'
    );

    this.jobService.getLocations().then((data: any) => {
      this.locations = [this.allLocationsOption, ...data];
    });

    const queryStatusFilterName = this.activeRouter.snapshot.queryParams['status'];
    if (queryStatusFilterName === 'upcoming') {
      this.activeTab.set('upcoming');
    } else if (queryStatusFilterName === 'past_due') {
      this.activeTab.set('past-due');
    }
    this.refreshData();
  }

  initFilterForm(): void {
    this.filterForm = this.fb.group({
      search: [''],
      location: [0]
    });

    this.filterForm.get('search')!.valueChanges.pipe(
      debounceTime(1000),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.upcomingCurrentPage.set(1);
      this.pastDueCurrentPage.set(1);
      this.upcomingTableParams.currentPage = 1;
      this.pastDueTableParams.currentPage = 1;
      this.refreshData();
    });

    this.filterForm.valueChanges.pipe(
      distinctUntilChanged((prev, curr) => prev.location === curr.location),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.upcomingCurrentPage.set(1);
      this.pastDueCurrentPage.set(1);
      this.upcomingTableParams.currentPage = 1;
      this.pastDueTableParams.currentPage = 1;
      this.refreshData();
    });
  }

  initServiceSchedulesObservable(
    subject: Subject<{ tableParams: TableParams, filterParams: any }>,
    tableDataSignal: WritableSignal<TableData>,
    loadingSignal: WritableSignal<boolean>,
    isUpcoming: boolean,
    altTableDataSignal: WritableSignal<TableData>,
    altLoadingSignal: WritableSignal<boolean>,
    altTab: 'past-due' | 'upcoming') {
    subject.pipe(
      distinctUntilChanged((prev, curr) => ObjectUtils.deepEqual(prev, curr)),
      tap(_ => loadingSignal.set(true)),
      auditTime(0),
      switchMap(params => {
        const {tableParams, filterParams} = params;
        const offset = (tableParams.currentPage - 1) * tableParams.pageSize;
        return this.scheduleService.getServiceSchedules(
          tableParams.pageSize,
          offset,
          filterParams.search || '',
          tableParams.sortParams,
          filterParams.location,
          isUpcoming
        );
      }),
      catchError(() => {
        loadingSignal.set(false);
        this.toastService.showToast('Failed to load service schedule', 'error');
        return of({status: 'error', data: {Data: []}, errors: []});
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((response: any) => {
      const data = response;
      if (!Array.isArray(data)) {
        console.error('API returned non-array data:', data);
        tableDataSignal.set({data: [], total: 0});
        return;
      }

      const schedules = data.map((item: any) => this.mapAssetData(item));

      const totalCount = data.length > 0 ? data[0].totalCount : 0;
      if (data.length > 0 && data[0].isUpcoming !== isUpcoming) {
        this.activeTab.set(altTab);
        tableDataSignal.set({data: [], total: 0});
        altTableDataSignal.set({data: schedules, total: totalCount});
        altLoadingSignal.set(false);
      } else {
        tableDataSignal.set({data: schedules, total: totalCount});
      }
      loadingSignal.set(false);
    });
  }

  mapAssetData(item: any): any {
    return {
      asset_id: item.assetId,
      is_active: item.isActive,
      is_sn_unknown: item.isSnUnknown,
      serial_number: item.serialNumber,
      asset_number: item.assetNumber,
      asset_model: item.modelName,
      model_id: item.modelId,
      asset_make: item.makeName,
      make_id: item.makeId,
      asset_type: item.typeName,
      type_id: item.typeId,
      model_name: `${item.modelName || ''} ${item.modelName || ''} ${item.typeName || ''}`.trim(),
      location: item.locationName,
      last_pm: DateUtils.formatDate(item.lastPMDate),
      next_pm: DateUtils.formatDate(item.nextPMDate),
      status: item.isUpcoming ? 'Upcoming' : 'Past Due',
      next_pm_status_color: item.isUpcoming ? '#ED7D31' : '#C02126',
      is_upcoming: item.isUpcoming
    };
  }

  changeTab(tab: 'past-due' | 'upcoming'): void {
    this.activeTab.set(tab);
    this.refreshData();
  }

  onUpcomingTableParamChange(params: TableParams): void {
    this.upcomingTableParams = params;
    const filterParams = {...this.filterForm.value};
    const tableParams = {...params};
    this.loadUpcoming$.next({tableParams, filterParams});
  }

  onPastDueTableParamChange(params: TableParams): void {
    this.pastDueTableParams = params;
    const filterParams = {...this.filterForm.value};
    const tableParams = {...params};
    this.loadPastDue$.next({tableParams, filterParams});
  }

  refreshData(): void {
    const filterParams = {...this.filterForm.value};
    if (this.activeTab() === 'past-due') {
      const tableParams = {...this.pastDueTableParams};
      this.loadPastDue$.next({tableParams, filterParams});
    } else {
      const tableParams = {...this.upcomingTableParams};
      this.loadUpcoming$.next({tableParams, filterParams});
    }
  }

  isSelectClearable(controlName: string) {
    return this.filterForm.get(controlName)?.value !== 0;
  }

  clearSelectValue(controlName: string) {
    this.filterForm.get(controlName)?.setValue(0);
  }

  hasPermission(permission: string): any {
    if (!this.currentUser) return false;
    const permissions = this.currentUser?.permission.split(',');
    return permissions.includes(permission);
  }
}
